import { useEffect, useRef } from 'react';

interface UseMarqueeOptions {
  speed?: number;
  direction?: 'left' | 'right';
  pauseOnHover?: boolean;
}

export const useMarquee = ({
  speed = 1,
  direction = 'left',
  pauseOnHover = true
}: UseMarqueeOptions = {}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const isPausedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;

    if (!container || !track) return;

    let position = 0;
    let trackWidth = 0;

    // Wait for content to load and calculate width
    const initializeAnimation = () => {
      trackWidth = track.scrollWidth / 2; // Half because we duplicate content
      
      if (trackWidth === 0) {
        // If width is still 0, wait a bit more
        setTimeout(initializeAnimation, 100);
        return;
      }

      const animate = () => {
        if (isPausedRef.current) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }

        position += direction === 'left' ? speed : -speed;
        
        // Reset position when we've scrolled exactly one set of content
        if (direction === 'left' && position >= trackWidth) {
          position = 0;
        } else if (direction === 'right' && position <= -trackWidth) {
          position = 0;
        }

        track.style.transform = `translateX(${position}px)`;
        animationRef.current = requestAnimationFrame(animate);
      };

      // Start animation
      animationRef.current = requestAnimationFrame(animate);
    };

    const handleMouseEnter = () => {
      if (pauseOnHover) {
        isPausedRef.current = true;
      }
    };

    const handleMouseLeave = () => {
      if (pauseOnHover) {
        isPausedRef.current = false;
      }
    };

    // Initialize animation after a short delay to ensure content is loaded
    const initTimeout = setTimeout(initializeAnimation, 100);

    // Add event listeners
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    // Cleanup
    return () => {
      clearTimeout(initTimeout);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [speed, direction, pauseOnHover]);

  return { containerRef, trackRef };
};
